<template>
  <VDropdown
    ref="popper"
    :shown="open"
    :triggers="[]"
    :auto-hide="false"
    :placement="placement"
    container="body"
    theme="sample-order-hint"
    @click="close('anchor')"
  >
    <slot />
    <template #popper>
      <div class="sample-order-hint">
        <img src="/images/sample-order.png" alt="" />
        <div class="hint-content">
          <p class="heading">{{ $t('SAMPLE_ORDER.HINT.HEADING') }}</p>
          <p>{{ $t('SAMPLE_ORDER.HINT.TEXT') }}</p>
        </div>
        <button
          class="link icon-btn close"
          :aria-label="$t('GENERAL.CLOSE')"
          @click.stop="close('close')"
        >
          <Icon icon="close" />
        </button>
      </div>
    </template>
  </VDropdown>
</template>

<script setup lang="ts">
/**
 * The first-time hint on the "Order Samples" entry point: it wraps the button it explains and opens
 * once that button has actually been on screen. The X is not in the Figma — nothing else there ends
 * the hint. See `docs/publishing/sample-order-journey.md`.
 */
import { computed, onMounted, onUnmounted, ref } from 'vue';
import {
  markSampleOrderHintShown,
  sampleOrderHintVisible,
} from 'commons/sampleOrder/sampleOrderHint';
import {
  SAMPLE_ORDER_SOURCE,
  trackSampleOrderHintDismissed,
  trackSampleOrderHintShown,
  type SampleOrderSource,
} from 'commons/sampleOrder/sampleOrderTracking';

const props = defineProps<{ source: SampleOrderSource }>();

const placement = computed(() =>
  props.source === SAMPLE_ORDER_SOURCE.PRODUCT_SETTINGS
    ? 'bottom-end'
    : 'bottom-start',
);

const popper = ref<{ $el: HTMLElement } | null>(null);
const open = ref(false);
let observer: IntersectionObserver | null = null;

/** `anchor` means the partner pressed the button the hint explains - a conversion. */
const close = (via: 'close' | 'anchor'): void => {
  if (open.value) {
    trackSampleOrderHintDismissed({ source: props.source, via });
  }
  open.value = false;
};

// Once, on mount: the intent flips right after a publish, and the hint must not appear then.
const eligible = sampleOrderHintVisible();

const anchor = (): HTMLElement | undefined => popper.value?.$el;

onMounted(() => {
  const el = anchor();

  // Without the observer the date would be spent on a hint below the fold.
  if (!eligible || !el || !window.IntersectionObserver) {
    return;
  }

  observer = new IntersectionObserver((entries) => {
    if (!entries[0].isIntersecting) {
      return;
    }

    observer?.disconnect();
    observer = null;
    open.value = true;
    markSampleOrderHintShown();
    trackSampleOrderHintShown({ source: props.source });
  });

  observer.observe(el);
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>

<!-- Not scoped: `container="body"` teleports the popper out of this component's tree. -->
<style lang="scss">
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';

.sample-order-hint {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  text-align: left;

  & > img {
    flex-shrink: 0;
    width: 75px;
    height: 75px;
    border-radius: $border-radius-medium;
    object-fit: cover;
  }

  p {
    margin: 0;
    color: $grey65;
  }

  .heading {
    margin-bottom: 4px;
    font-weight: bold;
    color: $grey80;
  }

  .close {
    flex-shrink: 0;
    color: $grey65;

    .icon {
      width: 16px;
      height: 16px;
    }
  }
}
</style>
